import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Check, X, ArrowRight, Loader2, Flame } from "lucide-react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | failed | timeout
  const [attempts, setAttempts] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    if (status !== "polling") return;
    if (attempts >= 8) { setStatus("timeout"); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") setStatus("paid");
        else if (["failed", "expired"].includes(r.data.payment_status)) setStatus("failed");
        else setAttempts(a => a + 1);
      } catch { setAttempts(a => a + 1); }
    }, attempts === 0 ? 500 : 2000);
    return () => clearTimeout(t);
  }, [sessionId, status, attempts]);

  return (
    <div className="min-h-screen bg-[#0B0B0F] flex items-center justify-center p-6">
      <div className="w-full max-w-md text-center">
        <div className="flex items-center justify-center gap-2 mb-8">
          <Flame className="w-6 h-6 text-[#FF3B30]" strokeWidth={2.5} />
          <span className="font-display font-black text-2xl uppercase">Fit<span className="text-[#FF3B30]">Command</span></span>
        </div>
        {status === "polling" && (
          <>
            <Loader2 className="w-16 h-16 mx-auto text-[#E2FF31] animate-spin mb-6" />
            <div className="font-display text-3xl font-black uppercase mb-2">Verifica in corso...</div>
            <div className="text-[#A0A0AB]">Stiamo confermando il pagamento con Stripe.</div>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-20 h-20 mx-auto bg-[#22C55E]/20 border border-[#22C55E] flex items-center justify-center mb-6">
              <Check className="w-10 h-10 text-[#22C55E]" strokeWidth={3} />
            </div>
            <div className="font-display text-4xl font-black uppercase mb-3">Pagamento OK<span className="text-[#FF3B30]">!</span></div>
            <div className="text-[#A0A0AB] mb-8">Il tuo abbonamento è attivo. Ottimo, atleta.</div>
            <button onClick={() => navigate("/")} data-testid="pay-success-cta" className="bg-[#FF3B30] text-white font-bold uppercase tracking-widest px-6 py-3 hover:bg-[#e02e24] transition-all inline-flex items-center gap-2">
              Vai al Comando <ArrowRight className="w-4 h-4" />
            </button>
          </>
        )}
        {status === "failed" && (
          <>
            <div className="w-20 h-20 mx-auto bg-[#EF4444]/20 border border-[#EF4444] flex items-center justify-center mb-6">
              <X className="w-10 h-10 text-[#EF4444]" strokeWidth={3} />
            </div>
            <div className="font-display text-3xl font-black uppercase mb-3">Pagamento non riuscito</div>
            <div className="text-[#A0A0AB] mb-6">Riprova o contatta il tuo coach.</div>
            <Link to="/subscribe" className="bg-[#FF3B30] text-white font-bold uppercase tracking-widest px-6 py-3 hover:bg-[#e02e24] transition-all inline-flex items-center gap-2">
              Riprova
            </Link>
          </>
        )}
        {status === "timeout" && (
          <>
            <div className="font-display text-2xl font-black uppercase mb-3">Ci sto mettendo di più del previsto</div>
            <div className="text-[#A0A0AB] mb-6">Il pagamento potrebbe essere ancora in elaborazione. Controlla tra qualche minuto sulla Dashboard.</div>
            <button onClick={() => navigate("/")} className="border border-white text-white font-bold uppercase tracking-widest px-6 py-3 hover:bg-white hover:text-black transition-all">
              Vai al Comando
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen bg-[#0B0B0F] flex items-center justify-center p-6">
      <div className="w-full max-w-md text-center">
        <div className="flex items-center justify-center gap-2 mb-8">
          <Flame className="w-6 h-6 text-[#FF3B30]" strokeWidth={2.5} />
          <span className="font-display font-black text-2xl uppercase">Fit<span className="text-[#FF3B30]">Command</span></span>
        </div>
        <div className="font-display text-3xl font-black uppercase mb-3">Pagamento annullato</div>
        <div className="text-[#A0A0AB] mb-8">Nessun addebito è stato effettuato. Torna quando vuoi.</div>
        <Link to="/subscribe" data-testid="pay-cancel-retry" className="bg-[#FF3B30] text-white font-bold uppercase tracking-widest px-6 py-3 hover:bg-[#e02e24] transition-all inline-flex items-center gap-2">
          Riprova <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
